import { Descriptions, Popover, Tag, Tooltip } from 'antd';
import { CheckOutlined, ExclamationOutlined } from '@ant-design/icons';
import type { ScrollOptions } from 'antd/lib/form/interface';
import type { FormInstance } from 'antd/es/form/Form';
import { addDays, endOfDay, format, parse, isValid } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import type { HolidaysTypes } from 'date-holidays';
import type {
  PartnerAddressV2,
  PartnerCarrierAccountV2,
  PartnerContact,
  PartnerLocation,
  PartnerTenant,
} from '@shipium-corp/shipium-ts-types';
import { formatPhoneNumber } from '../utils/PhoneField';
import type { ReturnToAddressOverride, ScheduleHoliday, SupportedInjectionDestination } from '../../store/assets/types';
import type {
  HydratedSchedule,
  HydratedThirdPartyBillingAccount,
  HydrationAssetError,
  ValidationInfo,
} from '../../store/fulfillmentContext/types';
import type { FulfillmentContextDetail, shippingDay } from '../../store/services/FP_FulfillmentContextService/types';
import { MaskedValue } from '../shared/MaskedValue';
import { ShipiumTag } from '../shared/ShipiumTag';
import type { Schedule, ScheduleEntry } from '../../store/services/FP_FulfillmentContextService/types';
import { getHolidatesForNextNYears, SUPPORTED_HOLIDAY_COUNTRIES } from './forms/sharedForms/ScheduleEntryForm';
import type { ValueType } from '../types';
import { capitalCase } from 'case-anything';
import type { PartnerCarrierRateSummary } from '~/services/partner-management-service';
import { Flex } from '../Flex';

export function sanitizeWholeNumber(val: number, max: number = Number.MAX_SAFE_INTEGER) {
  let num = Math.floor(val || 0); // nullish
  num = Math.max(Math.min(num, max), 0); // clamp [0, MAX_INT]

  return num;
}

export function hoursDaysDisplayFormatter(val: ValueType) {
  if (val === undefined) val = '0';

  const sanitizeHours = sanitizeWholeNumber(+val);
  const days = Math.floor(sanitizeHours / 24);
  const hoursRemaining = Math.floor(sanitizeHours % 24);

  let label = '';

  const dayStr = days === 1 ? 'Day' : 'Days';
  label += `${days} ${dayStr} `;

  const hourStr = hoursRemaining === 1 ? 'Hour' : 'Hours';
  label += `${hoursRemaining} ${hourStr}`;

  return label;
}

export function hoursDaysDisplayParser(val: string | undefined) {
  if (val === undefined) val = '0 Days 0 Hours';

  // ['1', 'Days', '5', 'Hours']
  const vals = val.split(' ');
  const days = Math.floor(+vals?.[0] || 0);
  const hours = Math.floor(+vals?.[2] || 0);

  const totalHours = Math.floor(days * 24) + Math.floor(hours);

  return String(totalHours);
}

export function hoursToDecimalDaysDisplayFormatter(val: ValueType) {
  if (val === undefined) val = '0';

  const sanitizeHours = sanitizeWholeNumber(+val);
  const decimal = (sanitizeHours / 24).toFixed(2);

  return String(decimal);
}

export function hoursToDecimalDaysDisplayParser(val: string | undefined) {
  if (val === undefined) val = '0';

  const hours = Math.round(+val * 24);

  return String(hours);
}

// convert 2.71 >> 65 (hours) >> to "2 Days 17 Hours"
export function decimalExpectedDaysAndTimeToShipToDisplay(expectedDaysAndTimeToShipValue: number | string | undefined) {
  if (!expectedDaysAndTimeToShipValue) expectedDaysAndTimeToShipValue = 0;

  return hoursDaysDisplayFormatter(hoursToDecimalDaysDisplayParser(String(expectedDaysAndTimeToShipValue)));
}

export function formatExpectedDaysAndTimeToShip(expectedDaysAndTimeToShipValue: number | string | undefined) {
  if (!expectedDaysAndTimeToShipValue) expectedDaysAndTimeToShipValue = 0;

  // converts hours to format "2.71"
  const expectedDaysAndTimeToShip = +hoursToDecimalDaysDisplayFormatter(String(expectedDaysAndTimeToShipValue));

  // round up to nearest day from fractional days to backfill expectedDaysToShip
  const expectedDaysToShip = Math.ceil(expectedDaysAndTimeToShip);

  return {
    expectedDaysAndTimeToShip,
    expectedDaysToShip,
  };
}

// typecast expectedDaysAndTimeToShipValue = "2.71" to Number and return expectedDaysToShip = 3 for deprecated field
export function formatDecimalExpectedDaysAndTimeToShipForApi(
  expectedDaysAndTimeToShipValue: number | string | undefined,
) {
  if (!expectedDaysAndTimeToShipValue) expectedDaysAndTimeToShipValue = 0;

  const expectedDaysAndTimeToShip = +expectedDaysAndTimeToShipValue;
  // round up to nearest day from fractional days to backfill expectedDaysToShip
  const expectedDaysToShip = Math.ceil(expectedDaysAndTimeToShip);

  return {
    expectedDaysAndTimeToShip,
    expectedDaysToShip,
  };
}

export const getScheduleEntryId = (scheduleEntry: ScheduleEntry) => {
  if (!scheduleEntry) {
    return;
  }
  return (
    scheduleEntry.scheduleEntryId || `${new Date(scheduleEntry.from).valueOf()}_${new Date(scheduleEntry.to).valueOf()}`
  );
};

export const getScheduleEntryFromId = (schedule: Schedule, scheduleEntryId: string | null) => {
  return scheduleEntryId
    ? schedule.entries.find((entry) => {
        return getScheduleEntryId(entry) === scheduleEntryId;
      })
    : undefined;
};

export interface DescriptionItemWithPopoverOptions {
  itemLabel: string;
  itemValue: string | React.ReactNode | React.ReactNode[];
  popOverTitle: string | React.ReactNode | React.ReactNode[];
  popOverContent: string | React.ReactNode | React.ReactNode[];
}

export const descriptionItemWithPopover = ({
  itemLabel,
  itemValue,
  popOverTitle,
  popOverContent,
}: DescriptionItemWithPopoverOptions) => {
  const popover = <div>{popOverContent}</div>;

  return (
    <Descriptions.Item label={itemLabel}>
      <Popover title={popOverTitle} content={popover}>
        {itemValue}
      </Popover>
    </Descriptions.Item>
  );
};

const addressLine = (id: string, line?: string, mask = false) => {
  if (line) {
    return <li key={id}>{mask ? <MaskedValue value={line} /> : line}</li>;
  }
};
export const formatAddressLocationItemsForList = (location?: PartnerLocation, fullDetail = false, maskPii = false) => {
  return (
    <>
      {fullDetail ? addressLine(`company`, location?.companyName) : undefined}
      {addressLine(`address-1`, location?.address1, maskPii)}
      {addressLine(`address-2`, location?.address2, maskPii)}
      {addressLine(`address-3`, location?.address3, maskPii)}
      {addressLine(`address-4`, location?.address4, maskPii)}
      <li key={`address-5`}>
        {location?.city}, {location?.region} {location?.postalCode} {location?.country}
      </li>
    </>
  );
};
export const formatAddressAsList = (address?: PartnerAddressV2, fullDetail = false) => {
  return address ? (
    <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
      {fullDetail ? addressLine(`${address.urn}-name`, address.name) : undefined}
      {formatAddressLocationItemsForList(address.location)}
    </ul>
  ) : (
    <ShipiumTag style={{ fontSize: 10, padding: 4, lineHeight: '10px' }} color={'Gold'}>
      No Address Configured
    </ShipiumTag>
  );
};

export const formattedContactAsList = (contact?: PartnerContact) => {
  return contact ? (
    <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
      <li>{contact.contactName}</li>
      <li>{contact.email}</li>
      <li>{formatPhoneNumber(contact.phoneNumber)}</li>
    </ul>
  ) : (
    <ShipiumTag style={{ fontSize: 10, padding: 4, lineHeight: '10px' }} color={'Gold'}>
      No Contact Configured
    </ShipiumTag>
  );
};

export const fomattedScheduleAsList = (schedule: HydratedSchedule | Schedule) => {
  return (
    <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
      <li>{schedule.name}</li>
      {schedule.description && <li>{schedule.description}</li>}
    </ul>
  );
};

export const validationStatusToMessage = (hydrationError: HydrationAssetError) => {
  // update these with defined enum/type
  const assetErrorTypeMap: Record<string, string> = {
    address: 'Address',
    contact: 'Contact',
    schedule: 'Schedule',
    carrierRate: 'Carrier Rate(s)',
  };
  const assetTxt = assetErrorTypeMap[hydrationError.assetType] ?? hydrationError.assetType;
  switch (hydrationError.error) {
    case 'Not configured':
      return `${assetTxt} is not configured`;
    case 'No active calendar entry':
      return `${assetTxt} has no active calendar entry`;
    case 'Not active':
      return `${assetTxt} is not active`;
  }
};
export const renderHydrationValidationStatus = (validationInfo: ValidationInfo) => {
  if (validationInfo.configurationValid) {
    return (
      <ShipiumTag color="Blue">
        <CheckOutlined /> Configuration Valid{' '}
      </ShipiumTag>
    );
  } else {
    return (
      <ShipiumTag color="Coral">
        {' '}
        <Tooltip
          title={
            <ul style={{ listStyle: 'none' }}>
              {validationInfo.errors.map((e) => (
                <li>{validationStatusToMessage(e)}</li>
              ))}
            </ul>
          }
        >
          <ExclamationOutlined /> Configuration Invalid
        </Tooltip>
      </ShipiumTag>
    );
  }
};

export const formattedReturnToAddressOverridesCarrierLinks = (returnAddressOverride?: ReturnToAddressOverride) => {
  const returnToAddressOverrideCarrierLinks = returnAddressOverride?.carrierIds || [];
  const carriers = returnToAddressOverrideCarrierLinks.map((carrier) => {
    return <li>{carrier}</li>;
  });

  return carriers.length > 0 ? (
    <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>{carriers}</ul>
  ) : (
    <span>(no linked configuration)</span>
  );
};

export const formattedTenantLinksList = (tenant?: PartnerTenant) => {
  const tenantLinks = tenant?.links || {};
  const linkKeys = Object.keys(tenantLinks ?? {}) || [];
  const links = linkKeys.map((key) => {
    return (
      <li>
        {tenantLinks[key].length} - {key}
      </li>
    );
  });

  return links.length > 0 ? (
    <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>{links}</ul>
  ) : (
    <span>(no linked configuration)</span>
  );
};

export const formatThirdPartyBillingAccountsAsList = (rates: HydratedThirdPartyBillingAccount[]) => {
  return (
    <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
      {rates
        .sort((r1, r2) => {
          return (r1.validationInfo.configurationValid ? -1 : 1) - (r2.validationInfo.configurationValid ? -1 : 1);
        })
        .slice(0, 2)
        .map((account) => (
          <li>
            {account.validationInfo.configurationValid && <ShipiumTag color={'Teal'}>{account.name}</ShipiumTag>}
            {!account.validationInfo.configurationValid && (
              <ShipiumTag color={'Coral'}>
                <Tooltip
                  title={
                    <ul style={{ listStyle: 'none' }}>
                      {account.validationInfo.errors.map((e) => (
                        <li>{validationStatusToMessage(e)}</li>
                      ))}
                    </ul>
                  }
                >
                  {`${account.name} - (${account.carrierId})`}
                </Tooltip>
              </ShipiumTag>
            )}
          </li>
        ))}
      {rates.length > 2 && (
        <li>
          <ShipiumTag color={'Teal'}>And {rates.length - 2} more accounts</ShipiumTag>
        </li>
      )}
    </ul>
  );
};

export const formatCarrierRatesAsList = (rates: PartnerCarrierRateSummary[]) => {
  return (
    <Flex direction="vertical" gap="xs">
      {rates.slice(0, 2).map((rate) => (
        <Tag key={rate.carrierServiceMethodId}>{rate.carrierServiceMethodId}</Tag>
      ))}
      {rates.length > 2 && <Tag>And {rates.length - 2} more methods</Tag>}
    </Flex>
  );
};
export const formatCarrierAccountAsList = (carrierAccount: PartnerCarrierAccountV2) => {
  return (
    <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
      <li>{carrierAccount.name}</li>
      <li>{carrierAccount.description}</li>
    </ul>
  );
};

export type OriginType = 'at_large' | 'fc' | 'store';
export const OriginTypeDisplayMap: Record<OriginType, string> = {
  at_large: 'At Large',
  fc: 'Fulfillment Center',
  store: 'Store',
};

export const labelPropertiesRotationMap: Record<string, string> = {
  rotate_0: 'No Rotation',
  rotate_90: '90 Degrees',
  rotate_180: '180 Degrees',
  rotate_270: '270 Degrees',
};

export const parseAugmentationIdFromUrl = (url: string) => {
  const regex = /^(service-method|carrier|global)_(.*)\.(.*)_(.*)/;
  const [str, scope, carrierId, serviceMethodId, augmentId] = regex.exec(url) || [];

  return {
    str,
    scope,
    carrierId,
    serviceMethodId,
    augmentId,
  };
};
export const generateCsvMatrixFromSupportedDestinations = (destinations: SupportedInjectionDestination[]) => {
  return destinations.reduce(
    (supportedDestinations: string[][], dest) => {
      return [...supportedDestinations, ...dest.postalCodes.map((code: any) => [dest.countryCode, code])];
    },
    [['Country', 'Postal Code']],
  );
};

export const generateSupportedDestinationsFromString = (csvString: string): SupportedInjectionDestination[] => {
  // Use a Map for better memory efficiency with large datasets
  const groupedByCountry = new Map<string, string[]>();

  // Parse header row to determine column indices
  const headerEndIndex = csvString.indexOf('\n');
  if (headerEndIndex === -1) {
    throw new Error('Invalid CSV format: no header row found');
  }

  const headerLine = csvString.slice(0, headerEndIndex).trim();
  const headers = headerLine.split(',').map((h) => h.trim().replace(/"/g, '').toLowerCase());

  const countryIndex = headers.findIndex((h) => h === 'country');
  const postalIndex = headers.findIndex((h) => h === 'postal code');

  if (countryIndex === -1) {
    throw new Error('CSV must contain a "Country" column');
  }
  if (postalIndex === -1) {
    throw new Error('CSV must contain a "Postal Code" column');
  }

  const startIndex = headerEndIndex + 1;
  let lineStart = startIndex;

  // Process line by line without splitting the entire string
  for (let i = startIndex; i < csvString.length; i++) {
    if (csvString[i] === '\n' || i === csvString.length - 1) {
      const line = csvString.slice(lineStart, i === csvString.length - 1 ? i + 1 : i).trim();

      if (line) {
        // Split the line by comma and extract values based on header positions
        const values = line.split(',').map((v) => v.trim().replace(/"/g, ''));

        const country = values[countryIndex] || '';
        const postal = values[postalIndex] || '';

        // Throw error if postal is provided without country
        if (!country && postal) {
          throw new Error('Postal code provided without country');
        }

        if (country) {
          // Always create entry for country, even if postal is empty
          if (!groupedByCountry.has(country)) {
            groupedByCountry.set(country, []);
          }
          // Only add postal if it's not empty
          if (postal) {
            groupedByCountry.get(country)!.push(postal);
          }
        }
      }

      lineStart = i + 1;
    }
  }

  // Convert Map to final format
  const result: SupportedInjectionDestination[] = [];
  for (const [countryCode, postalCodes] of groupedByCountry) {
    result.push({ countryCode, postalCodes });
  }

  // Clear the Map to help with garbage collection
  groupedByCountry.clear();

  return result;
};

export const activeEntryForSchedule = (schedule?: Schedule) => {
  if (!schedule) {
    return undefined;
  }
  for (const entry of schedule.entries) {
    const from = new Date(entry.from);
    const to = new Date(entry.to);
    const now = new Date();
    const active = now >= from && now <= to;
    if (active) {
      return entry;
    }
  }
  return undefined;
};
export interface ScheduleEntryFormData {
  shippingDays: shippingDay[];
  weekendCutoff?: Date;
  shippingCutoff: Date;
  effectiveDates: Date[];
  configuredHolidays: string[];
  expectedDaysToShip?: number;
  expectedDaysAndTimeToShip: number;
  holidays: { date: Date; name?: string }[];
  otsTagsEnabled?: boolean;
  otsOverrideEnabled?: boolean;
  otsOverrideDaysAndTimeToShip?: number | string;
  otsOverrideExpiration?: Date;
}
export const formatProcessingCutoffInfo = (shippingCutoff: Date, weekendCutoff?: Date) => {
  const result: any = {
    shippingCutoff: format(shippingCutoff, 'HH:mm'),
    shippingCutoffHour: +format(shippingCutoff, 'H'),
  };

  // An Invalid Date is truthy but makes format() throw RangeError, so validity must be
  // checked, not just presence.
  if (weekendCutoff && isValid(weekendCutoff)) {
    result.weekendCutoff = format(weekendCutoff, 'HH:mm');
    result.weekendCutoffHour = +format(weekendCutoff, 'H');
  }

  return result;
};
interface ParseProcessingInfoParams {
  shippingCutoffHour?: number;
  shippingCutoff?: string;
  weekendCutoff?: string;
  weekendCutoffHour?: number;
}
export const parseProcessingInfo = ({
  shippingCutoffHour,
  shippingCutoff,
  weekendCutoff,
  weekendCutoffHour,
}: ParseProcessingInfoParams) => {
  return {
    shippingCutoff: shippingCutoff
      ? parse(shippingCutoff, 'HH:mm:ss', new Date())
      : new Date(new Date().setHours(shippingCutoffHour!, 0, 0)),
    // Return undefined when no weekend cutoff exists: setHours(undefined, ...) yields an
    // Invalid Date, which renders as a blank TimePicker that rejects input and throws on save.
    weekendCutoff: weekendCutoff
      ? parse(weekendCutoff, 'HH:mm:ss', new Date())
      : weekendCutoffHour !== undefined
        ? new Date(new Date().setHours(weekendCutoffHour, 0, 0))
        : undefined,
  };
};
// The tags checkbox is not always mounted — it comes off screen for an unentitled partner and on the
// simulation path — and an unmounted field reads as undefined. Absence means "not edited" and carries
// the stored value forward; only an explicit false is the user unticking it.
export const formatOtsProcessingFlags = (
  fData: Pick<ScheduleEntryFormData, 'otsTagsEnabled'>,
  existingEntry?: ScheduleEntry | null,
): { otsTagsEnabled: boolean } => {
  const otsTagsEnabled = fData.otsTagsEnabled ?? existingEntry?.processingCutoff?.otsTagsEnabled ?? false;
  return { otsTagsEnabled: !!otsTagsEnabled };
};

export interface OtsOverrideFields {
  otsOverrideDaysAndTimeToShip: number | null;
  otsOverrideExpiration: string | null;
}

const NO_OVERRIDE: OtsOverrideFields = { otsOverrideDaysAndTimeToShip: null, otsOverrideExpiration: null };

// The override toggle is the presence signal, so absence is never inferred from the value being falsy.
// Deliberately not modelled on formatExpectedDaysAndTimeToShip, whose `if (!value) value = 0` would make a
// same-day override (0) indistinguishable from no override at all.
//
// Setting an override is how a schedule opts out of order-to-ship, so nothing here may gate the override
// on some other flag — that would make the opt-out unexpressible on exactly the entries that need it.
export const formatOtsOverride = (
  fData: Pick<ScheduleEntryFormData, 'otsOverrideEnabled' | 'otsOverrideDaysAndTimeToShip' | 'otsOverrideExpiration'>,
  existingEntry?: ScheduleEntry | null,
): OtsOverrideFields => {
  // The override controls sit behind the partner OTS toggle, so they are not always mounted, and an
  // unmounted field reads as undefined. Absence means "not edited" and carries the stored override
  // forward; only an explicit false is the user switching it off. Mirrors formatOtsProcessingFlags.
  if (fData.otsOverrideEnabled === undefined) {
    const stored = existingEntry?.processingCutoff;
    return {
      otsOverrideDaysAndTimeToShip: stored?.otsOverrideDaysAndTimeToShip ?? null,
      otsOverrideExpiration: stored?.otsOverrideExpiration ?? null,
    };
  }

  if (!fData.otsOverrideEnabled) {
    return NO_OVERRIDE;
  }

  const hours = fData.otsOverrideDaysAndTimeToShip;
  if (hours === null || hours === undefined || hours === '') {
    return NO_OVERRIDE;
  }

  return {
    otsOverrideDaysAndTimeToShip: +hoursToDecimalDaysDisplayFormatter(String(hours)),
    // Presence is not enough: an Invalid Date is truthy and makes toISOString() throw RangeError,
    // which would fail the whole save. Same guard as formatProcessingCutoffInfo above.
    otsOverrideExpiration:
      fData.otsOverrideExpiration && isValid(fData.otsOverrideExpiration)
        ? fData.otsOverrideExpiration.toISOString()
        : null,
  };
};

export interface OtsOverrideFormValues {
  otsOverrideEnabled?: boolean;
  otsOverrideDaysAndTimeToShip?: number;
  otsOverrideExpiration?: Date;
}

export const OTS_OVERRIDE_DEFAULT_EXPIRY_DAYS = 3;

/**
 * Builds the end date pre-filled when an OTS override is switched on.
 *
 * An override with no end date keeps replacing the calculated value until someone removes it, so the
 * bounded configuration is the default one and clearing it is left to a deliberate act. The result is
 * end of day {@link OTS_OVERRIDE_DEFAULT_EXPIRY_DAYS} days out rather than "now plus three days", so
 * the value lands on a boundary instead of an arbitrary clock time.
 *
 * @param timeZoneId IANA zone to take end-of-day in, normally the origin's. Omit to use the browser's.
 * @returns the UTC instant to store; the picker converts it for display via `isoToZonedPickerDate`
 */
export const defaultOtsOverrideExpiration = (timeZoneId?: string): Date => {
  const nowThere = timeZoneId ? toZonedTime(new Date(), timeZoneId) : new Date();
  const endOfDayThere = endOfDay(addDays(nowThere, OTS_OVERRIDE_DEFAULT_EXPIRY_DAYS));
  return timeZoneId ? fromZonedTime(endOfDayThere, timeZoneId) : endOfDayThere;
};

// Presence is decided by null/undefined only, so a persisted 0 (same-day) loads as an ACTIVE override
// rather than as no override at all.
export const parseOtsOverrideForForm = (
  processingCutoff:
    | {
        otsOverrideDaysAndTimeToShip?: number | null;
        otsOverrideExpiration?: string | null;
      }
    | undefined,
  // Required, not defaulted: this gates a commercial entitlement, so a call site that forgets it
  // should fail to compile rather than silently get the permissive answer.
  otsEntitled: boolean,
): OtsOverrideFormValues => {
  // Deliberately NOT gated on entitlement: reading a stored override back is idempotent — the same
  // value is written out again — and the entitlement flag resolves asynchronously, so gating this
  // would leave an entry that HAS an override rendering as Active whenever the flag lands after the
  // form captured its initial values.
  const stored = processingCutoff?.otsOverrideDaysAndTimeToShip;
  if (stored !== null && stored !== undefined) {
    return {
      otsOverrideEnabled: true,
      otsOverrideDaysAndTimeToShip: +hoursToDecimalDaysDisplayParser(String(stored)),
      otsOverrideExpiration: processingCutoff?.otsOverrideExpiration
        ? new Date(processingCutoff.otsOverrideExpiration)
        : undefined,
    };
  }

  // An unentitled partner has no override control on screen, so nothing below can be something they
  // asked for. Omitting the keys routes formatOtsOverride to its preserve branch and leaves the entry
  // exactly as stored; reporting the override off would make it delete.
  if (!otsEntitled) {
    return {};
  }

  // Both keys are returned explicitly rather than omitted. Callers feed this to setFieldsValue, which
  // MERGES: omitting them leaves the previously selected entry's override sitting in the form store,
  // where it reads as this entry's own and saves onto it.
  return { otsOverrideEnabled: false, otsOverrideDaysAndTimeToShip: undefined, otsOverrideExpiration: undefined };
};

export const formatScheduleEntryFormData = (
  fData: ScheduleEntryFormData,
  existingEntry?: ScheduleEntry | null,
): ScheduleEntry => {
  const dateMap: Record<string, HolidaysTypes.Holiday[]> = {};
  for (const val of SUPPORTED_HOLIDAY_COUNTRIES) {
    const countryMap: Record<string, HolidaysTypes.Holiday[]> = {};
    for (const h of getHolidatesForNextNYears(val, 20)) {
      if (!countryMap[h.name]) {
        countryMap[h.name] = [];
      }
      countryMap[h.name].push(h);
    }
    Object.assign(dateMap, countryMap);
  }
  const {
    shippingDays,
    weekendCutoff,
    shippingCutoff,
    expectedDaysAndTimeToShip: expectedDaysAndTimeToShipFormHours,
    effectiveDates,
    configuredHolidays,
    holidays,
    otsTagsEnabled,
  } = fData;

  const otsFlags = formatOtsProcessingFlags({ otsTagsEnabled }, existingEntry);

  return {
    shippingDays,
    processingCutoff: {
      ...formatProcessingCutoffInfo(shippingCutoff, weekendCutoff),
      ...formatExpectedDaysAndTimeToShip(expectedDaysAndTimeToShipFormHours),
      ...otsFlags,
      ...formatOtsOverride(fData, existingEntry),
    },
    from: effectiveDates[0].toISOString(),
    to: effectiveDates[1].toISOString(),
    holidays: dropUnnamedHolidaysOnCoveredDates([
      ...(configuredHolidays?.flatMap((val: string) => {
        return dateMap[val]?.flatMap((r) => ({
          date: r.date?.split?.(' ')?.[0],
          metadata: { name: r.name },
        }));
      }) || []),
      ...(holidays
        ?.filter?.((val) => !!val?.date)
        .map((val: any) => {
          return {
            date: format(val?.date, 'yyyy-MM-dd'),
            metadata: { name: val?.name },
          };
        }) || []),
    ]),
  };
};

// An unnamed holiday (stored without a name) adds nothing on a date another entry already covers, e.g. an
// unnamed 12-25 next to a selected "Christmas Day". Named entries are kept as the user entered them.
const dropUnnamedHolidaysOnCoveredDates = (holidays: ScheduleHoliday[]) => {
  const namedDates = new Set(holidays.filter((h) => h.metadata?.name).map((h) => h.date));
  const unnamedDates = new Set<string>();
  return holidays.filter((h) => {
    if (h.metadata?.name) {
      return true;
    }
    if (namedDates.has(h.date) || unnamedDates.has(h.date)) {
      return false;
    }
    unnamedDates.add(h.date);
    return true;
  });
};

export const getInjectionContracts = (context: FulfillmentContextDetail) =>
  context.injectionProfiles
    ?.flatMap((ip) => ip.injectionSites)
    .flatMap((is) => is.contract)
    .filter((contract) => !!contract) ?? [];
export const getInjectionAndContextContracts = (context: FulfillmentContextDetail) => {
  const injectionContracts = getInjectionContracts(context);
  const allContracts = [...context.contracts, ...injectionContracts];
  return allContracts;
};

export interface BuildReturnToLinkParams {
  partnerUrl?: string | null;
  fulfillmentContextId?: string | null;
  tab?: string | null;
  subTab?: string | null;
}
// this needs to be any typed because
// of the navigate param typing
export const buildReturnToLink = ({ partnerUrl, fulfillmentContextId, tab, subTab }: BuildReturnToLinkParams): any => {
  if (!partnerUrl || !fulfillmentContextId) {
    return -1;
  }
  let url = `/${partnerUrl}/fulfillment-contexts/${fulfillmentContextId}`;
  if (tab) {
    url += `/${tab}`;
  }
  if (tab && subTab) {
    url += `/${subTab}`;
  }
  return url;
};

export const defaultAssetTableSortComparator = (a: any, b: any) => {
  // don't like my nested ternary? join the ci team
  return a.name ? (b.name ? a.name.trim().localeCompare(b.name?.trim()) : -1) : 1;
};

// Single source for the filter predicate: CountryCodeDropdown applies the same check to its own options.
export { isCountryAllowed } from '../shared/CountryCodeDropdown';

export const trimObjectStrings = (obj: any): { [key: string]: any } => {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(trimObjectStrings);
  }

  return Object.entries(obj ?? {}).reduce(
    (acc, [key, value]) => {
      acc[key] = typeof value === 'string' ? value.trim() : trimObjectStrings(value); // recursively trim nested objects
      return acc;
    },
    {} as { [key: string]: any },
  );
};

// A named holiday repeats every year and counts once per name. An unnamed one (written outside the
// Console, with a date only) has nothing to group by, so it counts once per date.
export const getUniqueHolidays = (holidays: ScheduleHoliday[]) => {
  return Array.from(new Set(holidays.map((holiday) => holiday.metadata?.name || holiday.date)));
};

export const getHolidaysConfiguredLabel = (holidays?: ScheduleHoliday[]) => {
  const count = holidays?.length ? getUniqueHolidays(holidays).length : 0;
  if (count === 0) {
    return 'No holidays configured';
  }
  return `${count} ${count === 1 ? 'holiday' : 'holidays'} configured`;
};

export const errorScrollBehavior: ScrollOptions = { behavior: 'smooth', block: 'center' };

export const scrollToFieldWithError = (form: FormInstance, fieldName: string) => {
  form.scrollToField(fieldName, errorScrollBehavior);
};

export const safeCapitalCase = (
  stringToFormat?: string,
  options?: { keepSpecialCharacters?: boolean | undefined; keep?: string[] | undefined },
) => {
  try {
    if (stringToFormat) {
      return capitalCase(stringToFormat, options);
    }
    return stringToFormat;
  } catch (err) {
    return stringToFormat;
  }
};

export const getOriginTimeZoneId = (context?: FulfillmentContextDetail): string | undefined =>
  context?.origin?.address?.address?.location?.validatedAddressMetadata?.timeZoneId;
